// Fase-kopjes in een nieuw Teams-projectkanaal (Joris, 3 nov 2026): de berichten "Preproductie",
// "Productie" en "Postproductie" als thread-starter, zodat iedereen per fase onder het juiste bericht reageert.
//
// Een app mag in Teams geen gewone kanaalberichten plaatsen (ChannelMessage.Send bestaat alleen als
// delegated recht). Daarom post de browser van de PL ze met zijn eigen Graph-token, één kopje per keer:
//   kopjes/volgende → de engine kijkt eerst in het kanaal wat er al staat en geeft het eerstvolgende
//   ontbrekende kopje vrij (met poging-id); de browser post precies dat bericht; kopjes/bevestig → de engine
//   controleert het bericht in Graph. Een onzekere afloop blijft 'onderweg' en wordt de volgende keer in het
//   kanaal teruggevonden — er komt nooit iets dubbel. Zie docs/teams-kanaal-automatisch.md.
import { AI_ENGINE_URL } from './apiConfig';
import { authFetch } from './authHelpers';
import { acquireGraphToken } from './graphToken';

const GRAPH = 'https://graph.microsoft.com/v1.0';
export const KOPJES_SCOPE = 'ChannelMessage.Send';
const MAX_RONDES = 12;
// Een vrijgave die al zo lang ligt (tabblad gepauzeerd, laptop dicht) gebruiken we niet meer: de engine kan
// het kopje intussen als onzeker hebben gemarkeerd. Melden als 'mislukt' (er is dan zeker niets gepost).
const VRIJGAVE_MAX_MS = 60 * 1000;
const pad = (id, stap) => `${AI_ENGINE_URL}/api/v1/teams/kanaal/project/${encodeURIComponent(id)}/kopjes/${stap}`;

async function engine(id, stap, body) {
  const r = await authFetch(pad(id, stap), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`);
  return d;
}

async function graphFout(r) {
  const t = await r.text().catch(() => '');
  let m = '';
  try { m = JSON.parse(t)?.error?.message || ''; } catch { m = t; }
  return `Teams ${r.status}${m ? `: ${String(m).slice(0, 160)}` : ''}`;
}

/**
 * Plaatst de ontbrekende fase-kopjes namens de ingelogde gebruiker, in volgorde (bovenaan = eerste).
 * Eerst het token (een eventuele toestemmingspopup vóór er iets vrijgegeven wordt), dan per kopje
 * volgende → post → bevestig. Stopt bij de eerste fout.
 * @returns {{ geplaatst: string[], fout: string|null, status: string|null }}
 */
/** Na controle in Teams ("staat er niet"): een onzekere plaatsing vrijgeven, zodat hij opnieuw kan. */
export async function vergeetKopje(projectRecId, kop) {
  return engine(projectRecId, 'vergeet', { kop });
}

export async function plaatsKopjes(projectRecId, { fetchImpl = fetch, tokenImpl = acquireGraphToken, nu = Date.now } = {}) {
  let token;
  try {
    token = await tokenImpl([KOPJES_SCOPE]);
  } catch (e) {
    return { geplaatst: [], fout: `geen toestemming voor ${KOPJES_SCOPE} (${e?.message || e})`, status: null };
  }
  const geplaatst = [];
  let status = null;
  for (let ronde = 0; ronde < MAX_RONDES; ronde += 1) {
    let v;
    const gevraagd = nu();
    try {
      v = await engine(projectRecId, 'volgende');
    } catch (e) {
      return { geplaatst, fout: e.message, status };
    }
    if (v.klaar) return { geplaatst, fout: null, status: v.kopjes_status || 'geplaatst' };

    let uitkomst = 'onzeker';
    let messageId = null;
    let fout = '';
    // Gemeten vanaf het verzoek: lag de vrijgave lang onderweg (tabblad gepauzeerd), dan niet meer posten.
    if (nu() - gevraagd > VRIJGAVE_MAX_MS) {
      await engine(projectRecId, 'bevestig', { poging: v.poging, uitkomst: 'mislukt', fout: 'vrijgave verlopen, niet gepost' }).catch(() => {});
      return { geplaatst, fout: `${v.kop}: vrijgave verlopen — probeer het opnieuw`, status };
    }
    try {
      const r = await fetchImpl(`${GRAPH}/teams/${v.team_id}/channels/${v.channel_id}/messages`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: { contentType: 'html', content: v.html } }),
      });
      if (r.ok) {
        const d = await r.json().catch(() => ({}));
        if (d?.id) { uitkomst = 'geplaatst'; messageId = String(d.id); } else fout = 'Teams gaf geen bericht-id terug';
      } else {
        fout = await graphFout(r);
        // 4xx = Teams weigerde (zeker niet geplaatst); 5xx = onbekend of hij er staat.
        uitkomst = r.status >= 400 && r.status < 500 ? 'mislukt' : 'onzeker';
      }
    } catch (e) {
      fout = `netwerk: ${e?.message || e}`;
    }
    let b;
    try {
      b = await engine(projectRecId, 'bevestig', {
        poging: v.poging, uitkomst, ...(messageId ? { message_id: messageId } : {}), ...(fout ? { fout } : {}),
      });
    } catch (e) {
      // Het bericht staat er misschien al; de volgende keer vindt de engine het in het kanaal terug.
      return { geplaatst, fout: `${v.kop}: mogelijk geplaatst, maar niet vastgelegd (${e.message})`, status };
    }
    status = b.kopjes_status || status;
    if (uitkomst !== 'geplaatst' || b.fout) return { geplaatst, fout: b.fout || `${v.kop}: ${fout}`, status };
    geplaatst.push(v.kop);
  }
  return { geplaatst, fout: 'te veel rondes — controleer het kanaal', status };
}
